import { refetchInboxMessageSchema, socketEvent } from "@orsane/schema/realtime";
import { useQueryClient } from "@tanstack/react-query";
import { Inbox, Logs, Target } from "lucide-react";
import { memo, Suspense, useRef } from "react";
import {
    Sidebar,
    SidebarContent,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuBadge,
    SidebarMenuItem,
} from "@/components/ui/sidebar";
import { useFaviconBadge } from "@/hooks/useFaviconBadge";
import { useSocketMessage } from "@/lib/socket/SocketContext";
import { useNotificationCount } from "@/queries/notification-queries";
import LoadingSpinner from "../loading-spinner";
import { trpc } from "../QueryProvider";
import { AppSidebarHeader } from "./AppSidebarHeader";
import { SidebarFooterComponent } from "./SidebarFooterComponent";
import { StaticSidebarButton } from "./StaticSidebarButton";
import { TaskListsSection } from "./TaskListsSection";

export const AppSidebar = memo(() => {
    // const { open: newTaskDialogOpen, setOpen: setNewTaskDialogOpen } = useNewTaskStore();
    const { data: notificationCount } = useNotificationCount();
    useFaviconBadge(!!notificationCount && notificationCount > 0);
    const queryClient = useQueryClient();
    const scrollRef = useRef<HTMLDivElement>(null);

    useSocketMessage({
        event: socketEvent.refetch_inbox,
        schema: refetchInboxMessageSchema,
        callback: () => {
            console.log("[socket] refetch_inbox received");
            queryClient.invalidateQueries(trpc.notification.list.queryOptions());
            queryClient.invalidateQueries(trpc.notification.notificationCount.queryOptions());
        },
    });

    const handleNavigationClick = () => {
        // refresh the tasks when clicking on a list item
        queryClient.invalidateQueries({
            queryKey: trpc.task.list.pathKey(),
        });
    };

    return (
        <>
            <Sidebar collapsible="none" className="relative w-full">
                <AppSidebarHeader />
                <SidebarContent ref={scrollRef} className="sidebar-scrollbar h-full">
                    <SidebarGroup className="pr-0">
                        <SidebarGroupLabel>Shortcuts</SidebarGroupLabel>
                        <SidebarGroupContent>
                            <SidebarMenu className="gap-0.5">
                                <SidebarMenuItem onClick={handleNavigationClick}>
                                    <StaticSidebarButton to="/inbox" label="Inbox" icon={Inbox} />
                                    {!!notificationCount && notificationCount > 0 && (
                                        <SidebarMenuBadge className="bg-sky-200 text-sky-800! px-1.5 text-xs">
                                            {notificationCount}
                                        </SidebarMenuBadge>
                                    )}
                                </SidebarMenuItem>
                                <SidebarMenuItem onClick={handleNavigationClick}>
                                    <StaticSidebarButton to="/assigned-to-me" label="Assigned to me" icon={Target} />
                                </SidebarMenuItem>
                                <SidebarMenuItem onClick={handleNavigationClick}>
                                    <StaticSidebarButton to="/all" label="All tasks" icon={Logs} />
                                </SidebarMenuItem>
                            </SidebarMenu>
                        </SidebarGroupContent>
                    </SidebarGroup>

                    <Suspense
                        fallback={
                            <SidebarGroup className="pr-0">
                                <SidebarGroupLabel>Lists</SidebarGroupLabel>
                                <SidebarGroupContent>
                                    <LoadingSpinner className="ml-2 mt-2" />
                                </SidebarGroupContent>
                            </SidebarGroup>
                        }
                    >
                        <TaskListsSection />
                    </Suspense>
                </SidebarContent>

                <SidebarFooterComponent />
            </Sidebar>

            {/* <NewTaskDialog open={newTaskDialogOpen} onOpenChange={setNewTaskDialogOpen} /> */}
        </>
    );
});
AppSidebar.displayName = "AppSidebar";
